import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, useDisclosure } from '@chakra-ui/react';
import {
  useLazyGetLogsQuery,
  useLazyGetFaqLogsQuery,
  useLazyGetScheduleLogsQuery,
  useLazyGetFinancialLogsQuery,
  useAppSelector,
} from '@spartalabs/pdc-core';
import { Container, LoadingModal, Unauthorized } from 'components';
import TableCard from 'components/Cards/TableCard';
import { useCustomToast } from 'hooks';
import { checkDateRange } from 'utils/functions';
import { DetailsModal } from './Components/DetailsModal';
import { FilterDrawer } from './Components/Filter';

export const Logs: React.FC = () => {
  const globalDate = useAppSelector(state => state.globalDataSlicer.globalDate);
  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );
  const [dateRange, setDateRange] = useState(checkDateRange(globalDate));
  const [filter, setFilter] = useState('');

  const { isOpen, onClose, onOpen } = useDisclosure();
  const [modalInfo, setModalInfo] = useState<
    { name: string; value: string }[] | null
  >(null);

  const { handleToast } = useCustomToast();

  const [isModalOpen, setModalOpen] = useState(false);

  const [
    fetchTable,
    {
      data: tableData,
      isLoading: isLoadingTable,
      isFetching: isFetchingTable,
      isError: isErrorTable,
    },
  ] = useLazyGetLogsQuery();
  const [
    fetchSchedule,
    { isLoading: isScheLoading, isFetching: isScheFetching },
  ] = useLazyGetScheduleLogsQuery();

  const [fetchFaq, { isLoading: isFaqLoading, isFetching: isFaqFetching }] =
    useLazyGetFaqLogsQuery();

  const [
    fetchFinan,
    { isLoading: isFinanLoading, isFetching: isFinanFetching },
  ] = useLazyGetFinancialLogsQuery();

  const handleModalInfo = useCallback(
    async (id: string) => {
      if (!tableData) return;

      const pickedInfo = tableData.itens.filter(
        (item: { id: string }) => item.id === id,
      );

      try {
        if (pickedInfo[0].section === 'Agendamento') {
          const schedule = await fetchSchedule(id).unwrap();
          setModalInfo(schedule);
        }
        if (pickedInfo[0].section === 'Central de Ajuda') {
          const faq = await fetchFaq(id).unwrap();
          setModalInfo(faq);
        }
        if (pickedInfo[0].section === 'Faturamento') {
          const finan = await fetchFinan(id).unwrap();
          setModalInfo(finan);
        }

        setModalOpen(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        handleToast({
          step: 4,
          action: 'exibir os detalhes',
        });
      }
    },
    [tableData],
  );

  const tableLabels: TableLabel[] = useMemo(
    () => [
      {
        title: 'Data',
        type: 'text',
        accessor: 'date',
        disableSorting: true,
      },
      {
        title: 'Horário',
        type: 'text',
        accessor: 'time',
        disableSorting: true,
      },
      {
        title: 'Nome do usuário',
        type: 'text',
        accessor: 'username',
        disableSorting: true,
      },
      {
        title: 'Ação',
        type: 'tag',
        accessor: 'action',
        disableSorting: true,
      },
      {
        title: 'Seção acessada',
        type: 'tag',
        accessor: 'section',
        disableSorting: true,
      },
      {
        title: 'Mais Detalhes',
        type: 'custom',
        accessor: 'id',
        disableSorting: true,
        component: id => (
          <Text
            as="button"
            textColor="text.100"
            onClick={() => handleModalInfo(id)}
            textDecoration="underline"
          >
            Ver detalhes
          </Text>
        ),
      },
    ],
    [tableData],
  );

  useEffect(() => {
    setDateRange(globalDate);
  }, [globalDate]);

  if (!permissions?.includes('logs')) return <Unauthorized />;

  return (
    <Container>
      <DetailsModal
        modalInfo={modalInfo}
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalInfo(null);
        }}
      />
      <LoadingModal
        isOpen={
          isFaqLoading ||
          isFaqFetching ||
          isScheLoading ||
          isScheFetching ||
          isFinanLoading ||
          isFinanFetching
        }
      />
      <FilterDrawer setFilter={setFilter} isOpen={isOpen} onClose={onClose} />

      <TableCard
        loading={isLoadingTable || isFetchingTable}
        fetchData={fetchTable}
        hasGlobalSearch
        customTitle={
          <Text
            alignSelf="center"
            color="text.100"
            fontSize="16px"
            fontWeight={400}
          >
            Log de <b>ações</b>
          </Text>
        }
        subtitle="Registro detalhado das atividades, eventos ou ações que ocorreram no sistema"
        overflowX="auto"
        w="100%"
        labels={tableLabels}
        data={tableData}
        initDate={dateRange}
        analyticsId="Todos os veículos"
        isError={isErrorTable}
        filter={filter}
        refetch={fetchTable}
        searchHolder="Faça sua busca pelo nome do usuário"
        handleFilter={onOpen}
      />
    </Container>
  );
};
